// Points rules as the website edits them: a club's in Club Settings, a meet's own in Edit Meet
// (docs/school-carnivals-points-slice-2.md in the portal repo). The shape is the API's PointsRulesDto:
//
//   { placePoints: [4, 3, 2, 1], takingPartForEveryone: false, eventTakingPartPoints: 1,
//     meetTakingPartPoints: 0, setSeasonBestPoints: 0, equalSeasonBestPoints: 0,
//     beatSeasonBestPoints: 0, excludeFirstSeasonBest: true, sharedTies: true,
//     relays: { placePoints: [8, 6, 4, 2], bestTeamOnly: false } }
//
// sharedTies (slice 3): tied athletes share the points of the places they fill (1, 1, 3), or, false,
// each gets the full points (1, 1, 2). Chosen in Club Settings only.
//
// relays (slice 4a): how relays score for an organisation with houses, or null (relays earn nothing):
// the points for each place, to the house, and whether only a house's best-placed team in a relay
// event scores. Offered only to an organisation with houses; the server refuses them for a club.
//
// While an admin types, a value can be '' (an empty box); the checks below refuse it, so Save never
// sends one. The checks are the server's (Services/Calculation/PointsRulesCheck.cs), with the same
// sentences, so a mistake shows before Save; the server's are the ones that hold.

import { placeLabel } from './meetPoints';
import { formatPoints } from './points';

export const MAX_PLACES = 30;
export const MAX_POINTS = 100;
export const MAX_SEASON_BEST_POINTS = 50;

const isWhole = (value) => value !== '' && value !== null && Number.isInteger(Number(value));
const inRange = (value, max) => isWhole(value) && Number(value) >= 0 && Number(value) <= max;

// Every problem with the rules, each a sentence an admin can act on. `seasonBestPoints` is whether the
// organisation is offered season-best points (a school is not: clubFeaturesOf(club).seasonBestPoints);
// `relaysOffered` whether it is offered relay points (it has houses: clubFeaturesOf(club).teams).
export const pointsRulesProblems = (rules, { seasonBestPoints = true, relaysOffered = false } = {}) => {
  if (!rules) return [];
  const problems = [];
  const places = rules.placePoints || [];

  if (places.length === 0) problems.push('Add at least one place that scores.');
  if (places.length > MAX_PLACES) problems.push(`Up to ${MAX_PLACES} places can score.`);

  places.forEach((points, index) => {
    if (!isWhole(points) || Number(points) < 1 || Number(points) > MAX_POINTS) {
      problems.push(
        `${placeLabel(index + 1)} place: the points must be a whole number from 1 to ${MAX_POINTS}.`
      );
    } else if (
      index > 0 &&
      isWhole(places[index - 1]) &&
      Number(points) > Number(places[index - 1])
    ) {
      problems.push(
        `${placeLabel(index + 1)} place earns more than ${placeLabel(index)} place. A lower place cannot earn more points.`
      );
    }
  });

  if (!inRange(rules.eventTakingPartPoints, MAX_POINTS)) {
    problems.push(`Points for taking part in an event must be from 0 to ${MAX_POINTS}.`);
  } else {
    const takingPart = Number(rules.eventTakingPartPoints);
    const last = places[places.length - 1];
    if (
      !rules.takingPartForEveryone &&
      takingPart > 0 &&
      places.length > 0 &&
      isWhole(last) &&
      Number(last) < takingPart
    ) {
      problems.push(
        `${placeLabel(places.length)} place earns ${Number(last)}, less than the ${takingPart} for taking part without a place. ` +
          'A placed athlete cannot earn less than an athlete who did not place.'
      );
    }
  }

  if (!inRange(rules.meetTakingPartPoints, MAX_POINTS)) {
    problems.push(`Points for taking part in the meet must be from 0 to ${MAX_POINTS}.`);
  }

  const seasonBestValues = [
    rules.setSeasonBestPoints,
    rules.equalSeasonBestPoints,
    rules.beatSeasonBestPoints,
  ];
  if (!seasonBestValues.every((value) => inRange(value, MAX_SEASON_BEST_POINTS))) {
    problems.push(`Season-best points must be from 0 to ${MAX_SEASON_BEST_POINTS}.`);
  } else if (!seasonBestPoints && seasonBestValues.some((value) => Number(value) > 0)) {
    problems.push('A school does not have season-best points.');
  }

  if (rules.relays) {
    if (!relaysOffered) {
      problems.push('A club does not have relay points.');
    } else {
      problems.push(...relayProblems(rules.relays.placePoints || []));
    }
  }

  return problems;
};

// The relay list's problems: the place list's checks, each sentence starting "Relays" (slice 4a).
// Relay teams earn no points for taking part, so the relay list is not held to them.
const relayProblems = (places) => {
  const problems = [];
  if (places.length === 0) problems.push('Relays: add at least one place that scores.');
  if (places.length > MAX_PLACES) problems.push(`Relays: up to ${MAX_PLACES} places can score.`);

  places.forEach((points, index) => {
    if (!isWhole(points) || Number(points) < 1 || Number(points) > MAX_POINTS) {
      problems.push(
        `Relays, ${placeLabel(index + 1)} place: the points must be a whole number from 1 to ${MAX_POINTS}.`
      );
    } else if (
      index > 0 &&
      isWhole(places[index - 1]) &&
      Number(points) > Number(places[index - 1])
    ) {
      problems.push(
        `Relays: ${placeLabel(index + 1)} place earns more than ${placeLabel(index)} place. A lower place cannot earn more points.`
      );
    }
  });
  return problems;
};

// The relays an admin starts with when they tick "Relays score points for the house" and there is no
// relay list yet: the same points as the place list, every team scoring (Doug, 2026-10-24: "Start with
// the same points as individuals").
export const startingRelays = (rules) => ({
  placePoints: [...(rules?.placePoints || [])],
  bestTeamOnly: false,
});

const pointsWord = (points) => `${points} ${Number(points) === 1 ? 'point' : 'points'}`;

// A tie for 1st with one other, both ways: the 1st place's points (full points), and the 1st and 2nd
// place points shared (a place beyond the list is worth 0). Values still being typed read as 0.
export const tieForFirstPoints = (rules) => {
  const places = rules?.placePoints || [];
  const n = (value) => (isWhole(value) ? Number(value) : 0);
  return { full: n(places[0]), shared: (n(places[0]) + n(places[1])) / 2 };
};

// What a tie for 1st earns each of two athletes under the rules' own choice. With taking part paid only
// to athletes without a place, a share worth less than taking part earns taking part (Doug, 2026-10-24:
// "whichever is more"); the worked example follows the same rule.
const tiedFirstPoints = (rules) => {
  const { full, shared } = tieForFirstPoints(rules);
  return rules.sharedTies ? shared : full;
};

// The ties setting as Club Settings shows it, and its two choices by name (Doug, 2026-10-24, T3: places
// are worked out first, and points follow from them). The popup before a changed rule is saved names
// them the same way.
export const TIES_SETTING = 'When athletes tie for a place';
export const TIES_CHOICES = {
  full: {
    name: 'Next place follows (1, 1, 2)',
    detail:
      "Tied athletes share a place, and the next athlete takes the next place. Each tied athlete gets that place's full points. " +
      'A tie for 1st: both earn 1st place points, and the next athlete is 2nd.',
  },
  shared: {
    name: 'Places skipped (1, 1, 3)',
    detail:
      "Tied athletes share a place, and the places they fill are skipped. They share those places' points. " +
      'A tie for 1st: they share the 1st and 2nd place points, and the next athlete is 3rd.',
  },
};

// The warning before a changed ties rule is saved (Club Settings, before the PIN; Doug's words of
// 2026-10-24, T3): which way the setting is changing, then what happens to this season's places and, from
// them, its points, with the organisation's own 1st and 2nd place points. `rules` are the rules being
// saved: their sharedTies is the new choice.
export const tiesChangeWarning = (rules) => {
  const toShared = Boolean(rules?.sharedTies);
  const { full, shared } = tieForFirstPoints(rules);
  return {
    title: 'Change how tied athletes are placed and scored?',
    setting: TIES_SETTING,
    from: toShared ? TIES_CHOICES.full.name : TIES_CHOICES.shared.name,
    to: toShared ? TIES_CHOICES.shared.name : TIES_CHOICES.full.name,
    intro:
      'This changes the results of every meet this season that already has results, not only future meets.',
    places: toShared
      ? 'Every athlete behind a tie moves down. With two athletes tied for 1st, the athlete who came 2nd becomes 3rd, ' +
        'and the one who came 3rd becomes 4th, out of the top three.'
      : 'Every athlete behind a tie moves up. With two athletes tied for 1st, the athlete who came 3rd becomes 2nd, ' +
        'and the one who came 4th becomes 3rd.',
    points: toShared
      ? `Tied athletes share the points of the places they fill (two tied for 1st get ${formatPoints(shared)} each instead of ${formatPoints(full)}), ` +
        "and every athlete who moves down earns the lower place's points."
      : `Each tied athlete gets the full points of their place (two tied for 1st get ${formatPoints(full)} each instead of ${formatPoints(shared)}), ` +
        "and every athlete who moves up earns the higher place's points.",
    when:
      'Places change on every results page as soon as you save. Season points are recalculated within an hour, or at once with Recalculate Points.',
    closing:
      "Meets of past seasons do not change. If you change this back later, this season's places and points change back too.",
    confirm: 'Change Places and Points',
  };
};

// The message after Club Settings is saved. `meetsPlacedAgain` is the server's count when the save
// changed the ties rule (null otherwise): the meets of this season whose places and points were updated
// (Doug's words of 2026-10-24, T3). `organisationTitle` is "Club", or "School" for a school (Doug,
// 2026-10-26).
export const clubSettingsSavedMessage = (meetsPlacedAgain, organisationTitle = 'Club') => {
  if (meetsPlacedAgain === null || meetsPlacedAgain === undefined) {
    return `${organisationTitle} settings updated successfully!`;
  }
  if (meetsPlacedAgain === 0) {
    return `${organisationTitle} settings saved. No meet this season has results yet, so no places or points changed.`;
  }
  return (
    `${organisationTitle} settings saved. Places and points were updated in ${meetsPlacedAgain} ` +
    `${meetsPlacedAgain === 1 ? 'meet' : 'meets'} of this season. ` +
    'Season points are recalculated within an hour, or at once with Recalculate Points.'
  );
};

// The rules in plain sentences, for the summary beside the editor. `groupWord` is "age group" or
// "year group" (useGroupLabels().words.lower.group); `houseWord` the organisation's word for a house,
// in a sentence ("house", "faction": teamWordsOf(club).lower.team). Values still being typed read as 0.
export const pointsRulesSummary = (
  rules,
  { groupWord = 'age group', seasonBestPoints = true, houseWord = 'house' } = {}
) => {
  if (!rules) return [];
  const n = (value) => (isWhole(value) ? Number(value) : 0);
  const lines = [];
  const places = rules.placePoints || [];

  if (places.length > 0) {
    lines.push(
      `Place points in each ${groupWord}: ` +
        `${places.map((points, index) => `${placeLabel(index + 1)} earns ${n(points)}`).join(', ')}. ` +
        'Lower places earn no place points.'
    );
  }

  const takingPart = n(rules.eventTakingPartPoints);
  if (takingPart === 0) {
    lines.push('Taking part in an event earns no points.');
  } else if (rules.takingPartForEveryone) {
    lines.push(
      `Everyone who takes part in an event earns ${pointsWord(takingPart)} more, placed or not. That includes a DNF and all fouls.`
    );
  } else {
    lines.push(
      `Taking part in an event without earning place points earns ${pointsWord(takingPart)}. That includes a DNF and all fouls.`
    );
  }

  if (n(rules.meetTakingPartPoints) > 0) {
    lines.push(`Taking part in the meet earns ${pointsWord(n(rules.meetTakingPartPoints))}, once.`);
  }

  // The ties rule, in the mock-up's words (slice 3).
  if (places.length > 0) {
    const tied = formatPoints(tiedFirstPoints(rules));
    if (rules.sharedTies) {
      const neverLess =
        !rules.takingPartForEveryone && takingPart > 0
          ? ' A tied athlete never earns less than the points for taking part.'
          : '';
      lines.push(
        `A tie for 1st: the 1st and 2nd place points are shared, ${tied} each, and the next athlete is 3rd (1, 1, 3).${neverLess}`
      );
    } else {
      lines.push(`A tie for 1st: both earn ${tied}, and the next athlete is 2nd (1, 1, 2).`);
    }
  }

  if (seasonBestPoints) {
    const set = rules.excludeFirstSeasonBest ? 0 : n(rules.setSeasonBestPoints);
    const equal = n(rules.equalSeasonBestPoints);
    const beat = n(rules.beatSeasonBestPoints);
    if (set > 0 || equal > 0 || beat > 0) {
      lines.push(`Season bests: set ${set}, equal ${equal}, beat ${beat}.`);
    }
  }

  // Relays, in the mock-up's words (slice 4a).
  const relayPlaces = rules.relays?.placePoints || [];
  if (relayPlaces.length > 0) {
    const teams = rules.relays.bestTeamOnly
      ? `Only a ${houseWord}'s best-placed team scores.`
      : `Every team of a ${houseWord} scores.`;
    lines.push(
      `A relay: ${relayPlaces.map((points, index) => `${placeLabel(index + 1)} ${n(points)}`).join(', ')}, ` +
        `to the ${houseWord}. ${teams}`
    );
  }

  lines.push("When an event has heats and a final, only the final's places score.");
  return lines;
};

// One athlete's meet under the rules, for the summary's worked example: a 2nd place, a long jump with
// every attempt a foul, a 6th place, and (slice 3, the mock-up's row) a tie for 1st with one other;
// then taking part in the meet. Each row is { label, detail, points }; `total` is the sum.
// Points with a half read as 3.5 (formatPoints).
export const pointsRulesExample = (rules) => {
  if (!rules) return { rows: [], total: 0 };
  const n = (value) => (isWhole(value) ? Number(value) : 0);
  const places = rules.placePoints || [];
  const takingPart = n(rules.eventTakingPartPoints);

  const event = (label, place) => {
    const placePoints = place && place <= places.length ? n(places[place - 1]) : 0;
    const listed = Boolean(place) && place <= places.length;
    const partPoints = rules.takingPartForEveryone || !listed ? takingPart : 0;
    return row(label, placePoints, partPoints);
  };

  const row = (label, placePoints, partPoints) => {
    const parts = [];
    if (placePoints > 0) parts.push(`${formatPoints(placePoints)} place`);
    if (partPoints > 0) parts.push(`${formatPoints(partPoints)} taking part`);
    const points = placePoints + partPoints;
    return {
      label,
      detail: parts.length > 1 ? `${parts.join(' + ')} = ${formatPoints(points)}` : parts[0] || '',
      points,
    };
  };

  // A tie for 1st with one other: the share (or the full points), and taking part as for any placed
  // athlete, except that a share worth less than taking part earns taking part.
  const tiedRow = () => {
    if (places.length === 0) return row('200m, tied 1st with one other', 0, takingPart);
    const share = tiedFirstPoints(rules);
    if (rules.takingPartForEveryone) return row('200m, tied 1st with one other', share, takingPart);
    if (rules.sharedTies && share < takingPart) return row('200m, tied 1st with one other', 0, takingPart);
    return row('200m, tied 1st with one other', share, 0);
  };

  const rows = [
    event('100m, 2nd', 2),
    event('Long jump, every attempt a foul', null),
    event('800m, 6th', 6),
    tiedRow(),
  ];
  if (n(rules.meetTakingPartPoints) > 0) {
    rows.push({ label: 'Taking part in the meet', detail: '', points: n(rules.meetTakingPartPoints) });
  }

  return { rows, total: rows.reduce((sum, row) => sum + row.points, 0) };
};

// The club's rules in one line, under "Use the club's points" in Edit Meet (the mock-up's words):
// "Set in Club Settings: 4/3/2/1/1, taking part 1." settingsTitle names the page the rules are set
// on: "School Settings" for a school (Doug, 2026-10-26).
export const pointsRulesShortLine = (rules, settingsTitle = 'Club Settings') => {
  if (!rules) return '';
  const places = (rules.placePoints || []).join('/');
  const takingPart = Number(rules.eventTakingPartPoints) || 0;
  const forEveryone = takingPart > 0 && rules.takingPartForEveryone ? ' for everyone' : '';
  return `Set in ${settingsTitle}: ${places}, taking part ${takingPart}${forEveryone}.`;
};

// The rules as the API takes them: every value a number.
export const toPointsRulesRequest = (rules) => ({
  placePoints: (rules.placePoints || []).map(Number),
  takingPartForEveryone: Boolean(rules.takingPartForEveryone),
  eventTakingPartPoints: Number(rules.eventTakingPartPoints) || 0,
  meetTakingPartPoints: Number(rules.meetTakingPartPoints) || 0,
  setSeasonBestPoints: Number(rules.setSeasonBestPoints) || 0,
  equalSeasonBestPoints: Number(rules.equalSeasonBestPoints) || 0,
  beatSeasonBestPoints: Number(rules.beatSeasonBestPoints) || 0,
  excludeFirstSeasonBest: Boolean(rules.excludeFirstSeasonBest),
  sharedTies: Boolean(rules.sharedTies),
  relays: rules.relays
    ? {
        placePoints: (rules.relays.placePoints || []).map(Number),
        bestTeamOnly: Boolean(rules.relays.bestTeamOnly),
      }
    : null,
});

// The rules as an organisation's pages show them, applied where a page loads them (Club Settings, and the
// club's points and a meet's own in New Meet and Edit Meet). A school is not offered season-best points:
// its Points settings hide them and the first-result choice, so points saved before they were hidden
// (Season School's 2, 1, 1 with the box unticked; Doug, 2026-10-27) are read as 0 and ticked. The page
// then checks and saves what the school can see, where the hidden values used to stop every Save. The
// server stores the same for a school (OrganizationFeatures.ExcludeFirstSeasonBest) and refuses
// season-best points from one. For a club, or before the rules arrive, the rules as they are.
export const pointsRulesForOrganisation = (rules, seasonBestPoints) =>
  !rules || seasonBestPoints
    ? rules
    : {
        ...rules,
        setSeasonBestPoints: 0,
        equalSeasonBestPoints: 0,
        beatSeasonBestPoints: 0,
        excludeFirstSeasonBest: true,
      };

// A copy to edit, so the rules given are never changed.
export const copyPointsRules = (rules) => ({
  ...rules,
  placePoints: [...(rules?.placePoints || [])],
  relays: rules?.relays
    ? { ...rules.relays, placePoints: [...(rules.relays.placePoints || [])] }
    : null,
});
